/*
  AiSourcesToggle — inline "Show sources" affordance that sits under an
  AI-drafted block and reveals the full list of source documents +
  timestamps the draft was grounded in.

  Renders nothing when ``sources`` is empty (so callers can drop it in
  unconditionally). Keyboard-accessible: full <button> semantics + focus
  ring, expands/collapses on Enter/Space, and every source row exposes
  a data-testid the testing agent can drive.

  ``sources`` shape (server-normalised, see ``ai_summary._coerce_sources``
  and ``handwriting_combined._coerce_combined_sources``):

      [{ kind, id, occurred_at, label, href? }, ...]
*/
import { useState } from "react";
import { ChevronDown, ChevronRight, FileText, Mic, ClipboardList,
         Activity, Ruler, Radio, MessageCircle, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";

// Icon per source kind — keeps the popover scannable at a glance.
const KIND_ICON = {
  alert: Radio,
  assessment: ClipboardCheck,
  soap_note: FileText,
  treatment_note: FileText,
  measurement: Ruler,
  body_chart: Activity,
  communication: MessageCircle,
  // combined-draft kinds
  handwriting: ClipboardList,
  audio: Mic,
  intake: FileText,
};

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16);
    return d.toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
}

export default function AiSourcesToggle({
  sources, label = "Show sources", className, testId = "ai-sources-toggle",
}) {
  const list = Array.isArray(sources) ? sources.filter(Boolean) : [];
  const [open, setOpen] = useState(false);
  if (list.length === 0) return null;

  const btnLabel = `${open ? "Hide" : label} · ${list.length}`;

  return (
    <div className={cn("mt-2", className)} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-1 text-2xs rounded-md px-2 py-1",
          "border border-zinc-200 bg-white text-zinc-700 hover:text-zinc-950",
          "hover:bg-zinc-50 transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1",
        )}
        data-testid={`${testId}-btn`}
      >
        {open
          ? <ChevronDown className="h-3 w-3" />
          : <ChevronRight className="h-3 w-3" />}
        {btnLabel}
      </button>
      {open && (
        <ul
          className="mt-1.5 space-y-1 border border-zinc-200 rounded-md bg-zinc-50/60 p-2"
          data-testid={`${testId}-list`}
        >
          {list.map((s, i) => {
            const Icon = KIND_ICON[s.kind] || FileText;
            const kindPretty = (s.kind || "source").replace(/_/g, " ");
            return (
              <li
                key={`${s.kind}:${s.id || s.label}:${i}`}
                className="flex items-start gap-2 text-2xs text-zinc-800"
                data-testid={`${testId}-item-${i}`}
              >
                <Icon className="h-3 w-3 text-zinc-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="uppercase tracking-wider text-2xs text-zinc-500 font-semibold">
                      {kindPretty}
                    </span>
                    <span className="text-zinc-900">{s.label}</span>
                  </div>
                  {s.occurred_at && (
                    <div className="text-2xs text-zinc-500 font-mono-num">
                      {fmtWhen(s.occurred_at)}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
